import * as React from "react";
import { Toast as RadixToast } from "radix-ui";
import { cn } from "./cn";

// Toast — the ONE place an action's outcome is reported. Built because every screen was
// firing mutations that reported nothing at all: a click either silently worked or silently
// failed, and the only way to tell was to watch the table for a value change. An action that
// gives no feedback is indistinguishable from a broken button.
//
// Deliberately reports FAILURE loudly and success briefly: a failed action the operator
// doesn't notice is the one that costs something.

export type ToastKind = "success" | "error" | "info";

export interface ToastMessage {
  id: number;
  kind: ToastKind;
  title: string;
  // Line breaks in body are kept (a toast that carries several lines lists them).
  body?: string;
  // key: a toast with a key REPLACES the one already showing under that key instead of adding
  // another. For a source that reports again and again (the alert feed): one toast kept current,
  // never a pile. Measured 2026-10-10: a node going down raised 18 error/critical alerts in three
  // minutes, each its own toast that stays until dismissed, each closed by a 12px ✕ — a column
  // taller than the screen that had to be clicked away one at a time.
  key?: string;
  action?: { label: string; onClick: () => void };
  // onDismiss runs when the operator closes it (or everything is dismissed), not when a keyed
  // toast is replaced.
  onDismiss?: () => void;
}

interface ToastContextValue {
  notify(msg: Omit<ToastMessage, "id">): void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = React.useState<ToastMessage[]>([]);
  const nextId = React.useRef(1);

  const notify = React.useCallback((msg: Omit<ToastMessage, "id">) => {
    setMessages((prev) => {
      if (msg.key) {
        const at = prev.findIndex((m) => m.key === msg.key);
        const shown = at >= 0 ? prev[at] : undefined;
        if (shown) {
          // Same id: the toast on screen changes its text, it does not close and reopen.
          const next = prev.slice();
          next[at] = { ...msg, id: shown.id };
          return next;
        }
      }
      return [...prev, { ...msg, id: nextId.current++ }];
    });
  }, []);

  // The callbacks of what is on screen, read at dismissal (a state updater must not call out).
  const live = React.useRef<ToastMessage[]>([]);
  live.current = messages;

  const dismiss = React.useCallback((id: number) => {
    live.current.find((m) => m.id === id)?.onDismiss?.();
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const dismissAll = React.useCallback(() => {
    for (const m of live.current) m.onDismiss?.();
    setMessages([]);
  }, []);

  const value = React.useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      <RadixToast.Provider swipeDirection="right">
        {children}
        {messages.length > 1 && (
          <RadixToast.Root
            key="dismiss-all"
            duration={Infinity}
            open
            onOpenChange={(open) => {
              if (!open) dismissAll(); // swiped away
            }}
            className="flex items-center justify-between rounded-md border border-line2 bg-surface px-3 py-2 shadow-lg"
          >
            <RadixToast.Title className="text-xs text-muted">{messages.length} messages</RadixToast.Title>
            <button
              type="button"
              onClick={dismissAll}
              className="rounded border border-line2 px-2 py-1 text-xs font-medium text-fg hover:bg-surface2"
            >
              Dismiss all
            </button>
          </RadixToast.Root>
        )}
        {messages.map((m) => (
          <RadixToast.Root
            key={m.id}
            // An error stays until dismissed. A success disappears on its own — the operator
            // already knows what they clicked; what they need is to not miss a failure.
            duration={m.kind === "error" ? Infinity : 4000}
            onOpenChange={(open) => {
              if (!open) dismiss(m.id);
            }}
            className={cn(
              // `relative` is load-bearing: the close button is absolutely positioned, and
              // without it the ✕ anchors to the viewport instead of the toast.
              "relative rounded-md border p-3 pr-10 shadow-lg",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              // OPAQUE tints (mixed into the surface colour, not laid over the page): a translucent
              // toast let the text underneath read through its own.
              m.kind === "error"
                ? "border-[color-mix(in_oklab,var(--ozp-crit)_35%,transparent)] bg-[color-mix(in_oklab,var(--ozp-crit)_12%,var(--ozp-surface))]"
                : m.kind === "success"
                  ? "border-[color-mix(in_oklab,var(--ozp-ok)_35%,transparent)] bg-[color-mix(in_oklab,var(--ozp-ok)_12%,var(--ozp-surface))]"
                  : "border-line2 bg-surface",
            )}
          >
            <RadixToast.Title
              className={cn(
                "text-sm font-medium",
                m.kind === "error"
                  ? "text-crit"
                  : m.kind === "success"
                    ? "text-ok"
                    : "text-fg ",
              )}
            >
              {m.title}
            </RadixToast.Title>
            {m.body && (
              <RadixToast.Description className="mt-1 text-xs whitespace-pre-line text-muted">
                {m.body}
              </RadixToast.Description>
            )}
            {m.action && (
              <RadixToast.Action asChild altText={m.action.label}>
                <button
                  type="button"
                  onClick={m.action.onClick}
                  className="mt-2 rounded border border-line2 px-2 py-1 text-xs font-medium text-fg hover:bg-surface2"
                >
                  {m.action.label}
                </button>
              </RadixToast.Action>
            )}
            {/* A target a finger or a hurried pointer can hit: 28px square, not a 12px glyph. */}
            <RadixToast.Close
              aria-label="Dismiss"
              className="absolute top-1 right-1 flex size-8 items-center justify-center rounded text-sm text-muted hover:bg-surface2 hover:text-fg pointer-coarse:size-10"
            >
              ✕
            </RadixToast.Close>
          </RadixToast.Root>
        ))}
        {/* Never taller than the screen: what does not fit scrolls, and "Dismiss all" is FIRST, so it is in view however long the column is. */}
        {/* Phone: across the top, under the header — the bottom is where the page's own buttons and
            the installed app's tab bar are, and a toast there covered the very table whose change
            it was announcing (measured 2026-11-02: "Test sent" over the device list). Desk: the
            bottom-right corner, out of the way. */}
        <RadixToast.Viewport className="fixed inset-x-0 top-[calc(env(safe-area-inset-top)+56px)] z-50 mx-auto flex max-h-[60dvh] w-full max-w-md flex-col gap-2 overflow-y-auto px-3 outline-none sm:inset-x-auto sm:top-auto sm:right-0 sm:bottom-0 sm:max-h-dvh sm:w-96 sm:p-4" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
